// src/utils/discountValidation.js
export function validateDiscount(d, kind, opts = {}) {
  const currencyDigits = Number.isFinite(opts.currencyDigits) ? opts.currencyDigits : 2;

  const errs = {};

  // Name
  if (!d.name?.trim()) errs.name = 'Required';
  else if (d.name.length > 120) errs.name = 'Max 120 characters';

  // Discount value
  const isPercent = !!d.percentage;
  const valNum = Number(d.value);
  if (Number.isNaN(valNum) || valNum <= 0) {
    errs.value = 'Must be greater than 0';
  } else if (isPercent) {
    if (valNum > 100) errs.value = 'Percentage cannot exceed 100';
  } else {
    // fixed-amount: enforce minor units
    const raw = String(d.value ?? '');
    const dp = raw.includes('.') ? raw.split('.')[1]?.length || 0 : 0;
    if (dp > Math.max(0, currencyDigits)) {
      errs.value = `Max ${currencyDigits} decimal place${currencyDigits === 1 ? '' : 's'} for your currency`;
    }
  }

  // Dates
  const sd = d.start_date ? new Date(d.start_date) : null;
  const ed = d.end_date ? new Date(d.end_date) : null;
  if (sd && isNaN(sd.getTime())) errs.start_date = 'Invalid date';
  if (ed && isNaN(ed.getTime())) errs.end_date = 'Invalid date';
  if (sd && ed && sd > ed) errs.end_date = 'End date must be after start date';

  // Uses & spend
  if (!Number.isInteger(Number(d.max_client_uses)) || Number(d.max_client_uses) < 1) {
    errs.max_client_uses = 'Min 1';
  }
  if (!Number.isInteger(Number(d.max_total_uses)) || Number(d.max_total_uses) < 0) {
    errs.max_total_uses = 'Min 0';
  }
  if (Number(d.min_spend) < 0) errs.min_spend = 'Min 0';

  // Scope
  if (!d.apply_all && (!Array.isArray(d.items) || d.items.length === 0)) {
    errs.items = `Select at least one ${kind === 'reservations' ? 'offering' : 'product'} or choose "apply to all"`;
  }

  // Coupon (format only — uniqueness handled at page level)
  if (d.coupon && !/^[A-Z0-9_-]{3,32}$/.test(d.coupon.trim().toUpperCase())) {
    errs.coupon = '3–32 chars, letters/numbers/_/-';
  }

  // Product perks (reservations only)
  if (kind === 'reservations') {
    // free_products: nothing to validate
    // discounted_products: [{ product_id, percent }]
    const arr = Array.isArray(d.discounted_products) ? d.discounted_products : [];
    const seen = new Set();
    for (const dp of arr) {
      if (!dp?.product_id) continue;
      // no dups
      if (seen.has(dp.product_id)) {
        errs[`discounted_products.${dp.product_id}`] = 'Duplicate product';
      } else {
        seen.add(dp.product_id);
      }
      const n = Number(dp.percent);
      if (Number.isNaN(n) || n <= 0 || n > 100) {
        errs[`discounted_products.${dp.product_id}`] = 'Percent 1–100';
      }
    }
    const free = new Set(Array.isArray(d.free_products) ? d.free_products : []);
    const discIds = new Set(arr.map(dp => dp.product_id).filter(Boolean));
    const overlap = [...free].filter(id => discIds.has(id));
    if (overlap.length > 0) {
      const msg = 'A product cannot be both free and discounted';
      errs.free_products = msg;
      errs.discounted_products = msg;
    }
  }
  return errs;
}

export function validateAll(list, kind, opts = {}) {
  const errorsByIndex = {};
  list.forEach((d, i) => {
    const e = validateDiscount(d, kind, opts);
    if (Object.keys(e).length) errorsByIndex[i] = e;
  });
  return errorsByIndex;
}

// page-level helper: coupon uniqueness across both tabs
export function couponUniqErrors(reservationsList, productsList) {
  const norm = v => (v ? String(v).trim().toUpperCase() : '');
  const all = [...reservationsList, ...productsList];
  const counts = new Map();
  all.forEach(d => {
    const c = norm(d.coupon);
    if (!c) return;
    counts.set(c, (counts.get(c) || 0) + 1);
  });
  const isDup = c => c && counts.get(norm(c)) > 1;

  // build index->errors for each list
  const resErrs = {};
  reservationsList.forEach((d, i) => {
    if (isDup(d.coupon)) resErrs[i] = { ...(resErrs[i] || {}), coupon: 'Coupon must be unique' };
  });
  const prodErrs = {};
  productsList.forEach((d, i) => {
    if (isDup(d.coupon)) prodErrs[i] = { ...(prodErrs[i] || {}), coupon: 'Coupon must be unique' };
  });
  return { reservations: resErrs, products: prodErrs };
}
